'use client';

import { useScroll, m, animate, useMotionValue, useSpring, useTransform } from 'framer-motion';
import { useEffect, useState, useRef } from 'react';
// @mui
import { styled, alpha, useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import { Button as MuiButton } from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';
import NextLink from 'next/link';
import useMediaQuery from '@mui/material/useMediaQuery';
import { signIn, useSession } from 'next-auth/react';

// layouts
import MainLayout from 'src/layouts/main';
// components
import ScrollProgress from 'src/components/scroll-progress';
import Iconify from 'src/components/iconify';
import PricingView from 'src/sections/pricing/view';
import { useInView } from 'react-intersection-observer';
import { MotionViewport, varFade } from 'src/components/animate';

// ----------------------------------------------------------------------

const COLOR_CRIMSON = '#FF6D00'; // Tactical Orange (kept variable name for convenience)
const COLOR_BLACK = '#000000';
const COLOR_SURFACE = '#0A0A0A';
const COLOR_SURFACE_LIGHT = '#151515';

// Grid Background with Scanning Laser
const GridScanner = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: 0,
  opacity: 0.3, // Increased grid opacity
  backgroundImage: `
    linear-gradient(${alpha('#fff', 0.15)} 1px, transparent 1px),
    linear-gradient(90deg, ${alpha('#fff', 0.15)} 1px, transparent 1px)
  `,
  backgroundSize: '40px 40px',
  maskImage: 'radial-gradient(ellipse at center, black 40%, transparent 90%)',
  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0, left: 0, right: 0, height: '150px', // Larger height for trailing gradient
    background: `linear-gradient(to bottom, transparent, ${alpha(COLOR_CRIMSON, 0.1)} 80%, ${COLOR_CRIMSON})`, // Sweeping gradient
    borderBottom: `2px solid ${COLOR_CRIMSON}`, // Bright leading edge
    boxShadow: `0 10px 30px 10px ${alpha(COLOR_CRIMSON, 0.6)}`, // Intense glow
    animation: 'scan 3s cubic-bezier(0.2, 0, 0.1, 1) infinite',
  },
  '@keyframes scan': {
    '0%': { transform: 'translateY(-200px)', opacity: 0 },
    '15%': { opacity: 1 },
    '85%': { opacity: 1 },
    '100%': { transform: 'translateY(1000px)', opacity: 0 },
  },
}));

// Sharp Button
const StyledSharpButton = styled(MuiButton)(({ theme }) => ({
  borderRadius: 0,
  textTransform: 'uppercase',
  letterSpacing: '0.1em',
  fontWeight: 700,
  position: 'relative',
  overflow: 'hidden',
  border: `1px solid ${COLOR_CRIMSON}`,
  background: 'transparent',
  color: COLOR_CRIMSON,
  transition: 'all 0.2s',
  '&:hover': {
    background: COLOR_CRIMSON,
    color: '#000',
    boxShadow: `0 0 20px ${alpha(COLOR_CRIMSON, 0.4)}`,
  },
}));

// Animated counter component
function AnimatedCounter({ value, suffix = '' }: { value: number; suffix?: string }) {
  const [displayValue, setDisplayValue] = useState(0);
  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.3 });

  useEffect(() => {
    if (inView) {
      const controls = animate(0, value, {
        duration: 1.5,
        ease: 'easeOut',
        onUpdate: (v) => setDisplayValue(Math.floor(v)),
      });
      return () => controls.stop();
    }
  }, [inView, value]);

  return (
    <span ref={ref} style={{ fontFamily: 'monospace', letterSpacing: '-0.05em' }}>
      {displayValue.toLocaleString()}{suffix}
    </span>
  );
}

// ----------------------------------------------------------------------

export default function HomeView() {
  const { scrollYProgress } = useScroll();
  const [activeFeature, setActiveFeature] = useState(0);

  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down('lg'));
  const { status, data: session } = useSession();
  const isDemoUser = session?.user?.role === 'demo';

  const handleFeatureChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveFeature(newValue);
  };

  return (
    <MainLayout>
      <ScrollProgress scrollYProgress={scrollYProgress} color="error" />

      {/* Sinister Global Override */}
      <Box sx={{ 
        bgcolor: COLOR_BLACK, 
        color: '#ffffff', 
        minHeight: '100vh',
        pb: 10,
        position: 'relative',
        '& .MuiTypography-root': { color: '#ffffff' },
        '& .text-secondary': { color: '#888888' },
        fontFamily: '"Inter", sans-serif',
      }}>

      <Container sx={{ pt: {xs:12, md:24}, p:2, position: 'relative', zIndex: 1 }}>
        
        {/* Hero Section */}
        <Box sx={{ position: 'relative', py: 10, mb: 10 }}>
          <GridScanner />
          <Box
            component={m.div}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: 'easeOut' }}
            sx={{ textAlign: 'center', maxWidth: '900px', mx: 'auto', position: 'relative', zIndex: 2 }}
          >
            <Typography
              variant="overline"
              sx={{ color: COLOR_CRIMSON, letterSpacing: '0.2em', mb: 2, display: 'block', fontSize: '1rem', fontWeight: 600 }}
            >
              System Online // Stalking Protocol: Active
            </Typography>

            <Typography
              variant="h1"
              sx={{
                mb: 3,
                fontWeight: 900,
                fontSize: { xs: '3.5rem', md: '6rem' },
                lineHeight: 1,
                letterSpacing: '-0.04em',
                textTransform: 'uppercase',
              }}
            >
              Stalk. <br/> Extract. <br/> <Box component="span" sx={{ color: COLOR_CRIMSON }}>Dominate.</Box>
            </Typography>

            <Typography
              variant="body1"
              className="text-secondary"
              sx={{
                mb: 6,
                maxWidth: '600px',
                mx: 'auto',
                fontSize: { xs: '1.1rem', md: '1.25rem' },
                lineHeight: 1.6,
                fontWeight: 400,
              }}
            >
              The definitive storefront stalking platform for elite Amazon sellers. Monitor top-tier competitor storefronts and extract their high-margin ASINs before the rest of the market catches on.
            </Typography>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} justifyContent="center" alignItems="center">
              <StyledSharpButton
                variant="outlined"
                size="large"
                component={NextLink}
                href={status === 'authenticated' ? '/dashboard' : '#pricing'}
                sx={{ px: 6, py: 2 }}
              >
                {status === 'authenticated' ? 'Return to Dashboard' : 'Start Stalking'}
              </StyledSharpButton>
              
              {/* {status !== 'authenticated' && (
                <StyledSharpButton
                  variant="outlined"
                  size="large"
                  component={NextLink}
                  href="/demo/storefronts"
                  sx={{ 
                    px: 6, 
                    py: 2,
                    borderColor: '#fff',
                    color: '#fff',
                    '&:hover': {
                      background: '#fff',
                      color: '#000',
                      boxShadow: `0 0 20px ${alpha('#fff', 0.4)}`,
                    }
                  }}
                >
                  Try Demo
                </StyledSharpButton>
              )} */}
            </Stack>
          </Box>
        </Box>

        {/* Tactical Stats */}
        <Box component={MotionViewport} sx={{ my: {xs:15, md:24} }}>
          <m.div variants={varFade().inUp}>
            <Grid container spacing={4} alignItems="stretch">
              <Grid xs={12} md={4} sx={{ display: 'flex' }}>
                <Box sx={{ p: 5, bgcolor: COLOR_SURFACE, border: `1px solid ${COLOR_SURFACE_LIGHT}`, flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                  <Typography variant="overline" sx={{ color: COLOR_CRIMSON, mb: 2, display: 'block' }}>TARGETS ACQUIRED</Typography>
                  <Typography variant="h2" sx={{ fontWeight: 700, mb: 1, fontFamily: 'monospace' }}>
                    <AnimatedCounter value={5147} />
                  </Typography>
                  <Typography variant="body2" className="text-secondary">Elite storefronts actively monitored.</Typography>
                </Box>
              </Grid>
              <Grid xs={12} md={4} sx={{ display: 'flex' }}>
                <Box sx={{ p: 5, bgcolor: COLOR_SURFACE, border: `1px solid ${COLOR_SURFACE_LIGHT}`, flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                  <Typography variant="overline" sx={{ color: COLOR_CRIMSON, mb: 2, display: 'block' }}>DATA POINTS EXTRACTED</Typography>
                  <Typography variant="h2" sx={{ fontWeight: 700, mb: 1, fontFamily: 'monospace' }}>
                    <AnimatedCounter value={5392104} />
                  </Typography>
                  <Typography variant="body2" className="text-secondary">ASINs, pricing history, and velocity metrics.</Typography>
                </Box>
              </Grid>
              <Grid xs={12} md={4} sx={{ display: 'flex' }}>
                <Box sx={{ p: 5, bgcolor: COLOR_SURFACE, border: `1px solid ${COLOR_SURFACE_LIGHT}`, flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                  <Typography variant="overline" sx={{ color: COLOR_CRIMSON, mb: 2, display: 'block' }}>INTELLIGENCE UPDATES</Typography>
                  <Typography variant="h2" sx={{ fontWeight: 700, mb: 1, fontFamily: 'monospace' }}>
                    24/7
                  </Typography>
                  <Typography variant="body2" className="text-secondary">Continuous scanning and processing.</Typography>
                </Box>
              </Grid>
            </Grid>
          </m.div>
        </Box>

        {/* Features Tabs (Minimal & Sharp) */}
        <Box id="features" component={MotionViewport} sx={{ my: {xs:15, md:24} }}>
          <m.div variants={varFade().inUp}>
            <Box sx={{ mb: 6 }}>
              <Typography variant="overline" sx={{ color: COLOR_CRIMSON, letterSpacing: '0.1em' }}>// CAPABILITIES</Typography>
              <Typography variant="h2" sx={{ mt: 1, fontWeight: 800, fontSize: { xs: '2.5rem', md: '3.5rem' }, textTransform: 'uppercase', letterSpacing: '-0.02em' }}>
                Tactical Advantage.
              </Typography>
            </Box>

            <Box sx={{
              bgcolor: COLOR_SURFACE,
              border: `1px solid ${COLOR_SURFACE_LIGHT}`,
            }}>
              <Tabs
                value={activeFeature}
                onChange={handleFeatureChange}
                variant={isSmall ? "scrollable" : "fullWidth"}
                scrollButtons="auto"
                sx={{
                  borderBottom: `1px solid ${COLOR_SURFACE_LIGHT}`,
                  '& .MuiTab-root': {
                    py: 3,
                    fontSize: '1rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.1em',
                    color: '#666',
                    fontWeight: 600,
                    transition: 'all 0.2s',
                    '&:hover': { color: '#fff', bgcolor: COLOR_SURFACE_LIGHT },
                    '&.Mui-selected': { color: COLOR_CRIMSON, bgcolor: alpha(COLOR_CRIMSON, 0.05) }
                  },
                  '& .MuiTabs-indicator': { bgcolor: COLOR_CRIMSON, height: 2 }
                }}
              >
                <Tab label={<Stack direction="row" spacing={1} alignItems="center"><Iconify icon="mdi:radar" /><span>Reconnaissance</span></Stack>} />
                <Tab label={<Stack direction="row" spacing={1} alignItems="center"><Iconify icon="mdi:database-search" /><span>Extraction</span></Stack>} />
                <Tab label={<Stack direction="row" spacing={1} alignItems="center"><Iconify icon="mdi:chart-line-variant" /><span>Execution</span></Stack>} />
              </Tabs>

              <Box sx={{ p: { xs: 4, md: 8 } }}>
                <Box sx={{ display: activeFeature === 0 ? 'block' : 'none' }}>
                  <m.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3 }}>
                    <Grid container spacing={8} alignItems="center">
                      <Grid xs={12} md={5}>
                        <Typography variant="h3" sx={{ mb: 3, fontWeight: 700, textTransform: 'uppercase' }}>Surveillance Network</Typography>
                        <Typography variant="body1" className="text-secondary" sx={{ mb: 5, lineHeight: 1.8 }}>
                          Deploy our systems against top Amazon sellers. The moment they list a new, highly profitable product, you receive the intel. No guesswork, just raw data.
                        </Typography>
                        <Stack spacing={2}>
                          {['Continuous Monitoring', 'Immediate Alerts', 'Storefront Mapping'].map(item => (
                            <Stack key={item} direction="row" spacing={2} alignItems="center">
                              <Box sx={{ width: 8, height: 8, bgcolor: COLOR_CRIMSON }} />
                              <Typography variant="body2" sx={{ fontWeight: 600, letterSpacing: '0.05em' }}>{item}</Typography>
                            </Stack>
                          ))}
                        </Stack>
                      </Grid>
                      <Grid xs={12} md={7}>
                        <Box sx={{ border: `1px solid ${COLOR_SURFACE_LIGHT}`, p: 1, bgcolor: '#000', aspectRatio: '16/10', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                           <img src="/assets/images/home/capabilities-1-newer.png" alt="Reconnaissance" style={{ width: '100%', height: '100%', objectFit: 'contain', borderRadius: 4 }} />
                        </Box>
                      </Grid>
                    </Grid>
                  </m.div>
                </Box>

                <Box sx={{ display: activeFeature === 1 ? 'block' : 'none' }}>
                  <m.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3 }}>
                    <Grid container spacing={8} alignItems="center">
                      <Grid xs={12} md={5}>
                        <Typography variant="h3" sx={{ mb: 3, fontWeight: 700, textTransform: 'uppercase' }}>Deep Analytics</Typography>
                        <Typography variant="body1" className="text-secondary" sx={{ mb: 5, lineHeight: 1.8 }}>
                          We don't just find the products; we extract the metrics. Analyze historical pricing trends, pinpoint sales velocity, and review BSR history through our stark, zero-latency interface.
                        </Typography>
                        <Stack spacing={2}>
                          {['Historical BSR', 'Pricing Trends', 'Velocity Estimation'].map(item => (
                            <Stack key={item} direction="row" spacing={2} alignItems="center">
                              <Box sx={{ width: 8, height: 8, bgcolor: COLOR_CRIMSON }} />
                              <Typography variant="body2" sx={{ fontWeight: 600, letterSpacing: '0.05em' }}>{item}</Typography>
                            </Stack>
                          ))}
                        </Stack>
                      </Grid>
                      <Grid xs={12} md={7}>
                        <Box sx={{ border: `1px solid ${COLOR_SURFACE_LIGHT}`, p: 1, bgcolor: '#000', aspectRatio: '16/10', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                           <img src="/assets/images/home/capabilities-3.png" alt="Extraction" style={{ width: '100%', height: '100%', objectFit: 'contain', borderRadius: 4 }} />
                        </Box>
                      </Grid>
                    </Grid>
                  </m.div>
                </Box>
                
                <Box sx={{ display: activeFeature === 2 ? 'block' : 'none' }}>
                  <m.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3 }}>
                    <Grid container spacing={8} alignItems="center">
                      <Grid xs={12} md={5}>
                        <Typography variant="h3" sx={{ mb: 3, fontWeight: 700, textTransform: 'uppercase' }}>Strike First</Typography>
                        <Typography variant="body1" className="text-secondary" sx={{ mb: 5, lineHeight: 1.8 }}>
                          Stop guessing what sells. By reverse-engineering the strategies of 7-figure operations, you can execute on high-probability products before the competition realizes what hit them.
                        </Typography>
                        <Stack spacing={2}>
                          {['Strategy Replication', 'Market Gap Analysis', 'Swift Execution'].map(item => (
                            <Stack key={item} direction="row" spacing={2} alignItems="center">
                              <Box sx={{ width: 8, height: 8, bgcolor: COLOR_CRIMSON }} />
                              <Typography variant="body2" sx={{ fontWeight: 600, letterSpacing: '0.05em' }}>{item}</Typography>
                            </Stack>
                          ))}
                        </Stack>
                      </Grid>
                      <Grid xs={12} md={7}>
                        <Box sx={{ border: `1px solid ${COLOR_SURFACE_LIGHT}`, p: 1, bgcolor: '#000', aspectRatio: '16/10', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                           <img src="/assets/images/home/capabilities-2.png" alt="Execution" style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'left top', borderRadius: 4 }} />
                        </Box>
                      </Grid>
                    </Grid>
                  </m.div>
                </Box>

              </Box>
            </Box>
          </m.div>
        </Box>

        {/* Brutalist Testimonials */}
        <Box component={MotionViewport} sx={{ my: {xs:15, md:24} }}>
          <m.div variants={varFade().inUp}>
            <Box sx={{ mb: 6 }}>
              <Typography variant="overline" sx={{ color: COLOR_CRIMSON, letterSpacing: '0.1em' }}>// FIELD REPORTS</Typography>
              <Typography variant="h2" sx={{ mt: 1, mb: 3, fontWeight: 800, fontSize: { xs: '2.5rem', md: '3.5rem' }, textTransform: 'uppercase', letterSpacing: '-0.02em' }}>
                Operative Feedback.
              </Typography>
            </Box>
            <Grid container spacing={2}>
              {[
                {
                  text: "It started making me money and more than paying for itself in a matter of hours. I can take stores that I want my Amazon store to be like and SSP will drop the building blocks directly into my lap.",
                  author: "chrisracic", role: "Elite Amazon Seller"
                },
                {
                  text: "I think what the SSP team has done here is created something that is going to blow its users out of the water. Flawless execution and incredible ROI.",
                  author: "dereklee_oahunt", role: "7-Figure Seller"
                }
              ].map((t, i) => (
                <Grid xs={12} md={6} key={i}>
                  <Box sx={{ bgcolor: COLOR_SURFACE, border: `1px solid ${COLOR_SURFACE_LIGHT}`, height: '100%', p: 5, position: 'relative' }}>
                    <Iconify icon="mdi:format-quote-open" sx={{ position: 'absolute', top: 20, right: 20, color: COLOR_SURFACE_LIGHT, width: 64, height: 64 }} />
                    <Typography variant="body1" sx={{ mb: 6, fontWeight: 400, lineHeight: 1.8, color: '#CCC', fontSize: '1.1rem', position: 'relative', zIndex: 1 }}>{t.text}</Typography>
                    <Box sx={{ width: 40, height: 1, bgcolor: COLOR_CRIMSON, mb: 3 }} />
                    <Box>
                      <Typography variant="body2" sx={{ color: '#fff', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em' }}>{t.author}</Typography>
                      <Typography variant="caption" sx={{ color: COLOR_CRIMSON, letterSpacing: '0.1em' }}>[{t.role}]</Typography>
                    </Box>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </m.div>
        </Box>

        {/* Pricing */}
        <Box component={MotionViewport} sx={{ 
          my: {xs: 15, md: 24},
          '& .MuiTypography-root': { color: '#fff' }, 
          '& .MuiCard-root': { bgcolor: COLOR_SURFACE, borderColor: COLOR_SURFACE_LIGHT, borderRadius: 0 } 
        }}>
          <m.div variants={varFade().inUp}>
            <PricingView id="pricing" />
          </m.div>
        </Box>

        {/* Final CTA */}
        <Box component={MotionViewport} sx={{ my: {xs:15, md:24} }}>
          <m.div variants={varFade().inUp}>
            <Box sx={{ textAlign: 'center', p: { xs: 6, md: 10 }, bgcolor: COLOR_SURFACE, border: `1px solid ${COLOR_CRIMSON}`, position: 'relative', overflow: 'hidden' }}>
              <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: '4px', bgcolor: COLOR_CRIMSON }} />
              <Box sx={{ position: 'relative', zIndex: 1 }}>
                <Typography variant="h2" sx={{ mb: 3, fontWeight: 900, fontSize: { xs: '2.5rem', md: '4rem' }, textTransform: 'uppercase', letterSpacing: '-0.02em' }}>
                  Outpace the <br/> Competition.
                </Typography>
                <Typography variant="body1" className="text-secondary" sx={{ mb: 6, maxWidth: '500px', mx: 'auto', fontSize: '1.1rem' }}>
                  Join elite Amazon sellers using Storefront Stalker Pro to monitor top competitors and uncover highly profitable ASINs.
                </Typography>
                <StyledSharpButton variant="outlined" size="large" component={NextLink} href="#pricing" sx={{ px: 8, py: 2, fontSize: '1.2rem' }}>
                  Get Started
                </StyledSharpButton>
              </Box>
            </Box>
          </m.div>
        </Box>

      </Container>
      </Box>
    </MainLayout>
  );
}